/**
 * Product-filter assignments: the attributes/statuses given to the products a filter matches.
 * Persisted in the criteria-builder shape the server's AssignmentSerializer reads (BW-8331):
 * an AND of one `hasStatuses` and one `productAttribute` per attribute.
 */
import type { FilterNodeValue } from './registry';

export interface AttributeAssignmentDraft {
	attributeId?: number;
	/** List-of-values attribute: the attribute value ids. */
	valueIds: number[];
	/** Single-value attribute: the raw value (text, or a number / 0-1 for numeric and boolean attributes). */
	value?: string | number;
}

export interface AssignmentsDraft {
	statusIds: number[];
	attributes: AttributeAssignmentDraft[];
}

const leaves = (node: FilterNodeValue | undefined): FilterNodeValue[] => {
	if (!node?.$type) return [];
	if (node.$type === 'and' || node.$type === 'or') return (Array.isArray(node.criteria) ? node.criteria : []).flatMap(leaves);
	return [node];
};

export function parseAssignments(json: unknown): AssignmentsDraft {
	const draft: AssignmentsDraft = { statusIds: [], attributes: [] };
	for (const leaf of leaves(json as FilterNodeValue | undefined)) {
		if (leaf.$type === 'hasStatuses') draft.statusIds.push(...(leaf.ids ?? []));
		else if (leaf.$type === 'productAttribute') {
			const attributeId = leaf.attributeId as number | undefined;
			if (Array.isArray(leaf.values)) draft.attributes.push({ attributeId, valueIds: leaf.values as number[] });
			else if (typeof leaf.value === 'string' || typeof leaf.value === 'number')
				draft.attributes.push({ attributeId, valueIds: [], value: leaf.value });
		}
	}
	return draft;
}

/** Drops incomplete rows; returns undefined when nothing is assigned (the server then clears the assignments). */
export function serializeAssignments(draft: AssignmentsDraft): FilterNodeValue | undefined {
	const criteria: FilterNodeValue[] = [];
	if (draft.statusIds.length) criteria.push({ $type: 'hasStatuses', ids: draft.statusIds });
	for (const a of draft.attributes) {
		if (!a.attributeId) continue;
		if (a.valueIds.length) criteria.push({ $type: 'productAttribute', attributeId: a.attributeId, op: 'in', values: a.valueIds });
		else if (a.value !== undefined && a.value !== '')
			criteria.push({ $type: 'productAttribute', attributeId: a.attributeId, op: '=', value: a.value });
	}
	return criteria.length ? { $type: 'and', criteria } : undefined;
}
